import {
  AuthError,
  AuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthCredential,
  OAuthProvider,
  TwitterAuthProvider,
  User
} from 'firebase/auth';

import {
  AuthProviderConfig,
  AuthProviderKind,
  PasswordAuthProviderConfig,
  SsoProviderConfig,
  SsoProviderKind
} from '@/types/ssoProvider';

type KindDefaults = {
  scopes: string[];
  customParameters?: Record<string, string>;
};

const KIND_DEFAULTS: Record<SsoProviderKind, KindDefaults> = {
  google: {
    scopes: ['email', 'profile'],
    customParameters: { prompt: 'select_account' }
  },
  facebook: {
    scopes: ['email', 'public_profile']
  },
  twitter: {
    scopes: []
  },
  oauth: {
    scopes: ['openid', 'email', 'profile'],
    customParameters: { prompt: 'select_account' }
  }
};

const APPLE_DEFAULTS: KindDefaults = {
  scopes: ['email', 'name']
};

const YAHOO_DEFAULTS: KindDefaults = {
  scopes: ['openid', 'email', 'profile']
};

/** Built-in Firebase provider ids when `firebaseProviderId` is omitted from config. */
const DEFAULT_FIREBASE_PROVIDER_IDS: Record<string, string> = {
  microsoft: 'microsoft.com',
  google: 'google.com',
  apple: 'apple.com',
  facebook: 'facebook.com',
  twitter: 'twitter.com',
  yahoo: 'yahoo.com'
};

/** Built-in logos when `iconUrl` is omitted from config. */
const DEFAULT_ICON_URLS: Record<string, string> = {
  'microsoft.com': 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg',
  'google.com': 'https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg',
  'apple.com': 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg',
  'facebook.com':
    'https://upload.wikimedia.org/wikipedia/commons/0/05/Facebook_Logo_%282019%29.png',
  'twitter.com': 'https://upload.wikimedia.org/wikipedia/commons/6/6f/Logo_of_Twitter.svg',
  'yahoo.com': 'https://upload.wikimedia.org/wikipedia/commons/3/3a/Yahoo%21_%282019%29.svg'
};

/** Built-in i18n keys when `labelKey` is omitted from config. */
const DEFAULT_LABEL_KEYS: Record<string, string> = {
  'microsoft.com': 'loginWithMicrosoft',
  'google.com': 'loginWithGoogle',
  'apple.com': 'loginWithApple',
  'facebook.com': 'loginWithFacebook',
  'twitter.com': 'loginWithTwitter',
  'yahoo.com': 'loginWithYahoo'
};

const resolveIconUrl = (
  firebaseProviderId: string,
  iconUrl: string | undefined
): string | undefined => {
  const configured = iconUrl?.trim();
  if (configured) {
    return configured;
  }
  return DEFAULT_ICON_URLS[firebaseProviderId];
};

const resolveLabelKey = (
  firebaseProviderId: string,
  labelKey: string | undefined
): string | undefined => {
  const configured = labelKey?.trim();
  if (configured) {
    return configured;
  }
  return DEFAULT_LABEL_KEYS[firebaseProviderId];
};

type ScopeCustomParamProvider = {
  addScope: (scope: string) => unknown;
  setCustomParameters: (params: Record<string, string>) => unknown;
};

type ProviderKindHandler = {
  create: (config: SsoProviderConfig) => AuthProvider;
  credentialFromError: (error: AuthError) => OAuthCredential | null;
};

const applyScopesAndCustomParameters = (
  provider: ScopeCustomParamProvider,
  config: SsoProviderConfig
): void => {
  (config.scopes ?? []).forEach((scope) => provider.addScope(scope));
  if (config.customParameters && Object.keys(config.customParameters).length > 0) {
    provider.setCustomParameters(config.customParameters);
  }
};

const PROVIDER_KINDS: Record<SsoProviderKind, ProviderKindHandler> = {
  google: {
    create: (config) => {
      const provider = new GoogleAuthProvider();
      applyScopesAndCustomParameters(provider, config);
      return provider;
    },
    credentialFromError: (error) => GoogleAuthProvider.credentialFromError(error)
  },
  facebook: {
    create: (config) => {
      const provider = new FacebookAuthProvider();
      applyScopesAndCustomParameters(provider, config);
      return provider;
    },
    credentialFromError: (error) => FacebookAuthProvider.credentialFromError(error)
  },
  twitter: {
    create: (config) => {
      const provider = new TwitterAuthProvider();
      if (config.customParameters && Object.keys(config.customParameters).length > 0) {
        provider.setCustomParameters(config.customParameters);
      }
      return provider;
    },
    credentialFromError: (error) => TwitterAuthProvider.credentialFromError(error)
  },
  oauth: {
    create: (config) => {
      const provider = new OAuthProvider(config.firebaseProviderId);
      applyScopesAndCustomParameters(provider, config);
      return provider;
    },
    credentialFromError: (error) =>
      OAuthProvider.credentialFromError(error) as OAuthCredential | null
  }
};

const inferProviderKind = (firebaseProviderId: string): SsoProviderKind => {
  if (firebaseProviderId === 'google.com') {
    return 'google';
  }
  if (firebaseProviderId === 'facebook.com') {
    return 'facebook';
  }
  if (firebaseProviderId === 'twitter.com') {
    return 'twitter';
  }
  return 'oauth';
};

const resolveKindDefaults = (firebaseProviderId: string, kind: SsoProviderKind): KindDefaults => {
  if (firebaseProviderId === 'apple.com') {
    return APPLE_DEFAULTS;
  }
  if (firebaseProviderId === 'yahoo.com') {
    return YAHOO_DEFAULTS;
  }
  return KIND_DEFAULTS[kind];
};

const isPasswordEntry = (provider: AuthProviderConfig): boolean =>
  provider.kind === 'password' || provider.id === 'password';

export const isPasswordProvider = (
  provider: PasswordAuthProviderConfig | SsoProviderConfig
): provider is PasswordAuthProviderConfig => provider.kind === 'password';

const normalizePasswordProvider = (
  provider: AuthProviderConfig
): PasswordAuthProviderConfig | null => {
  const id = provider.id?.trim();
  if (!id) {
    return null;
  }

  return {
    id,
    kind: 'password',
    hidden: provider.hidden === true,
    internal: provider.internal === true
  };
};

const normalizeSsoProvider = (provider: AuthProviderConfig): SsoProviderConfig | null => {
  if (isPasswordEntry(provider)) {
    return null;
  }

  const id = provider.id?.trim();
  if (!id) {
    return null;
  }

  const firebaseProviderId =
    provider.firebaseProviderId?.trim() || DEFAULT_FIREBASE_PROVIDER_IDS[id];
  if (!firebaseProviderId) {
    return null;
  }

  const kind =
    (provider.kind as SsoProviderKind | undefined) ?? inferProviderKind(firebaseProviderId);
  if (!PROVIDER_KINDS[kind]) {
    return null;
  }

  const defaults = resolveKindDefaults(firebaseProviderId, kind);
  const tenant = provider.tenant?.trim() || provider.customParameters?.tenant?.trim() || undefined;

  return {
    id,
    kind,
    firebaseProviderId,
    iconUrl: resolveIconUrl(firebaseProviderId, provider.iconUrl),
    hidden: provider.hidden === true,
    internal: provider.internal === true,
    labelKey: resolveLabelKey(firebaseProviderId, provider.labelKey),
    scopes: provider.scopes ?? defaults.scopes,
    customParameters: {
      ...defaults.customParameters,
      ...provider.customParameters,
      ...(tenant ? { tenant } : {})
    },
    clientId: provider.clientId?.trim() || undefined,
    tenant
  };
};

export type NormalizedAuthProvider = PasswordAuthProviderConfig | SsoProviderConfig;

const DEFAULT_AUTH_PROVIDERS: AuthProviderConfig[] = [{ id: 'password' }];

const parseAuthProviders = (): NormalizedAuthProvider[] => {
  const configured = window.__REACT_BOILERPLATE_APP.AUTH_PROVIDERS;
  // Missing/empty → legacy email/password only (prod 1 / pre-SSO configs).
  const source =
    Array.isArray(configured) && configured.length > 0 ? configured : DEFAULT_AUTH_PROVIDERS;

  return source
    .map((provider) => {
      if (isPasswordEntry(provider)) {
        return normalizePasswordProvider(provider);
      }
      return normalizeSsoProvider(provider);
    })
    .filter((provider): provider is NormalizedAuthProvider => provider !== null);
};

const authProviders: NormalizedAuthProvider[] = parseAuthProviders();

/** Federated providers only (Microsoft, Google, …). */
export const ssoProviders: SsoProviderConfig[] = authProviders.filter(
  (provider): provider is SsoProviderConfig => !isPasswordProvider(provider)
);

export const primarySsoProviders: SsoProviderConfig[] = ssoProviders.filter(
  (provider) => !provider.hidden
);

export const secondarySsoProviders: SsoProviderConfig[] = ssoProviders.filter(
  (provider) => provider.hidden
);

export const isPasswordLoginEnabled = authProviders.some(isPasswordProvider);

const passwordProvider = authProviders.find(isPasswordProvider);

/** Password is on the main login surface (default when `hidden` is omitted). */
export const isPasswordLoginPrimary = !!passwordProvider && !passwordProvider.hidden;

/** Password is behind the expand control. */
export const isPasswordLoginSecondary = !!passwordProvider && !!passwordProvider.hidden;

export const hasSecondaryAuthOptions = isPasswordLoginSecondary || secondarySsoProviders.length > 0;

export const hasInternalProviders = ssoProviders.some((provider) => provider.internal);

export const isSsoEnabled = ssoProviders.length > 0;

export const isPrimarySsoEnabled = primarySsoProviders.length > 0;

export const loginPrimaryProvider: NormalizedAuthProvider | undefined = authProviders[0];

export const loginSecondaryProviders: NormalizedAuthProvider[] = authProviders.slice(1);

export const isLoginPasswordPrimary = loginPrimaryProvider?.kind === 'password';

export const loginPrimarySsoProvider: SsoProviderConfig | undefined =
  loginPrimaryProvider && !isPasswordProvider(loginPrimaryProvider)
    ? loginPrimaryProvider
    : undefined;

export const hasLoginSecondaryProviders = loginSecondaryProviders.length > 0;

export const getSsoProviderById = (id: string): SsoProviderConfig | undefined =>
  ssoProviders.find((provider) => provider.id === id);

export const getSsoProviderByFirebaseId = (
  firebaseProviderId: string | null | undefined
): SsoProviderConfig | undefined => {
  if (!firebaseProviderId) {
    return undefined;
  }

  return ssoProviders.find(
    (provider: SsoProviderConfig) => provider.firebaseProviderId === firebaseProviderId
  );
};

export const isConfiguredSsoFirebaseProvider = (
  firebaseProviderId: string | null | undefined
): boolean => !!getSsoProviderByFirebaseId(firebaseProviderId);

/** Microsoft / Entra providers that enforce enterprise-app user assignment. */
export const isEntraSsoFirebaseProvider = (
  firebaseProviderId: string | null | undefined
): boolean => {
  if (!firebaseProviderId || !isConfiguredSsoFirebaseProvider(firebaseProviderId)) {
    return false;
  }

  if (firebaseProviderId === 'microsoft.com' || firebaseProviderId.startsWith('oidc.')) {
    return true;
  }

  const config = getSsoProviderByFirebaseId(firebaseProviderId);
  return !!config?.tenant || !!config?.customParameters?.tenant;
};

export const hasAuthProvider = (
  user: User | null | undefined,
  firebaseProviderId: string
): boolean => !!user?.providerData?.some((provider) => provider.providerId === firebaseProviderId);

export const isSsoAuthUser = (user: User | null | undefined): boolean =>
  ssoProviders.some((provider) => hasAuthProvider(user, provider.firebaseProviderId));

/**
 * Custom-token sessions still carry the linked provider on the Firebase user.
 * Treat that link as the sign-in provider so Entra rechecks and MFA rules stay intact.
 */
export const resolveEffectiveSignInProvider = (
  tokenSignInProvider: string | null | undefined,
  user: User | null | undefined
): string | null => {
  if (tokenSignInProvider && tokenSignInProvider !== 'custom') {
    return tokenSignInProvider;
  }

  const linkedSsoProvider = ssoProviders.find((provider) =>
    hasAuthProvider(user, provider.firebaseProviderId)
  );
  return linkedSsoProvider?.firebaseProviderId ?? tokenSignInProvider ?? null;
};

/** Skip Firebase MFA setup when the session signed in via a configured SSO provider. */
export const shouldEnforceFirebaseMfa = (signInProvider?: string | null): boolean =>
  !isConfiguredSsoFirebaseProvider(signInProvider);

export const createAuthProvider = (config: SsoProviderConfig): AuthProvider => {
  const kind = config.kind ?? inferProviderKind(config.firebaseProviderId);
  return PROVIDER_KINDS[kind].create(config);
};

/** Try each registered kind until one returns a credential (account-linking / invite fallbacks). */
export const getSsoCredentialFromError = (error: unknown): OAuthCredential | null => {
  const authError = error as AuthError;
  for (const handler of Object.values(PROVIDER_KINDS)) {
    const credential = handler.credentialFromError(authError);
    if (credential) {
      return credential;
    }
  }
  return null;
};

export type { AuthProviderKind };
